// @ts-nocheck
// Settings → Phone → Call flow. STEP 1: read-only, live.
//
// This draws the tenant's REAL answering rules, resolved the way a call is
// actually resolved, and checks them against the failures we have already had
// in production. There is no sample data in this file. If a tenant has no
// menu, the screen says there is no menu.
//
// Editing (draft → review → publish, version history, rollback, AI grants) is
// step 2. Nothing here writes.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../api/client';
import { buildFlow, validateFlow, NODE } from './callFlowModel';
import './call-flow.css';

const TYPE_LABEL = {
  [NODE.HOURS]: 'Hours',
  [NODE.MENU]: 'Menu',
  [NODE.RING]: 'Ring',
  [NODE.AI]: 'AI',
  [NODE.VOICEMAIL]: 'Voicemail',
};

function Arrow() {
  return (
    <div className="cf-arrow" aria-hidden="true">
      <svg width="22" height="12" viewBox="0 0 22 12" fill="none">
        <path d="M0 6h17m0 0-5-4.5M17 6l-5 4.5" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function NodeCard({ node, selected, onSelect }) {
  const bad = node.type === NODE.RING && node.members && node.members.some((m) => m.canRing === false);
  return (
    <button
      type="button"
      className={`cf-node cf-node--${node.type}${selected ? ' is-selected' : ''}${bad ? ' is-flagged' : ''}`}
      onClick={() => onSelect(node.id)}
      aria-pressed={selected}
    >
      <span className="cf-node-type">{TYPE_LABEL[node.type]}</span>
      <span className="cf-node-title">{node.title}</span>
      <span className="cf-node-detail">{node.detail}</span>
      {node.type === NODE.HOURS && (
        <span className="cf-node-detail">{node.timezone}</span>
      )}
      {node.type === NODE.MENU && (
        <span className="cf-keys">
          {Object.keys(node.options).map((k) => <span className="cf-key" key={k}>{k}</span>)}
        </span>
      )}
      {bad && (
        <span className="cf-node-warn">
          {node.members.filter((m) => m.canRing === false).length} can&rsquo;t ring
        </span>
      )}
    </button>
  );
}

function Inspector({ node, flow }) {
  if (!node) {
    return (
      <aside className="cf-inspector">
        <p className="cf-insp-empty">Select a step to see how it is configured.</p>
      </aside>
    );
  }
  return (
    <aside className="cf-inspector">
      <div className="cf-insp-title">
        <h4>{node.title}</h4>
        <span>{TYPE_LABEL[node.type]} · node “{node.id}”</span>
      </div>

      {node.type === NODE.HOURS && (
        <>
          <Field label="Open">{node.detail}</Field>
          <Field label="Timezone">{node.timezone}</Field>
          <Field label="Right now">
            {node.hours.known === false ? (
              // SAY WHICH VALUE IS WRONG AND WHO FIXES IT.
              //
              // `openNow` already works this out — it hands back
              // reason: 'timezone "EST." is not a real timezone' — and the
              // screen was throwing it away for a flat "Unknown", which reads
              // as "the system is confused" rather than "one field has a typo
              // in it". Hodges has sat on the literal "EST." (a trailing full
              // stop) and the phone page gave nobody a way to know that.
              <span className="cf-hours-unknown">
                Can’t tell — {node.hours.reason || 'the timezone is not valid'}.
                <br />
                Hours are being read in UTC until it’s fixed. Set a real zone
                (for Florida that’s <code>America/New_York</code>) in
                Settings → Hours.
              </span>
            ) : (
              node.hours.open ? 'Open' : 'Closed'
            )}
          </Field>
          <Field label="When open">{labelFor(node.next.open, flow)}</Field>
          <Field label="When closed">{labelFor(node.next.closed, flow)}</Field>
        </>
      )}

      {node.type === NODE.MENU && (
        <>
          {Object.entries(node.options).map(([k, dest]) => (
            <Field key={k} label={`Key ${k}`}>{dest || <em>routes nowhere</em>}</Field>
          ))}
        </>
      )}

      {node.type === NODE.RING && (
        <>
          <Field label="Rings in this order">
            <ol className="cf-ringlist">
              {node.members.map((m) => (
                <li key={m.userId} className={m.canRing === false ? 'is-dead' : ''}>
                  <span className="cf-ring-who">
                    <b>{m.name}</b>
                    <span>
                      {m.appKnown ? (m.hasApp ? 'app' : 'no app') : 'app status unknown'}
                      {m.fallback ? ` · backup ${m.fallback}` : (m.hasApp === false ? ' · no backup number' : '')}
                      {m.extension ? ` · x${m.extension}` : ''}
                    </span>
                  </span>
                  <span className="cf-ring-to">{m.canRing === false ? 'skipped' : `${m.timeout || node.timeout}s`}</span>
                </li>
              ))}
              {!node.members.length && <li><em>No members</em></li>}
            </ol>
          </Field>
          <Field label="If nobody answers">{labelFor(node.next.noAnswer, flow)}</Field>
        </>
      )}

      {node.type === NODE.AI && (
        <>
          <Field label="Where it sits">{node.detail}</Field>
          <Field label="Opens with">{node.greeting || <em>the built-in greeting</em>}</Field>
          <Field label="What it may do">
            <span className="cf-muted">
              Not configured yet — the assistant is bounded by its prompt alone.
              Tool grants arrive with editing.
            </span>
          </Field>
          <Field label="Hands off to">{labelFor(node.next.escalate, flow)}</Field>
          <Field label="Recording disclosure">
            {(flow.profile.recording || {}).enabled
              ? ((flow.profile.recording || {}).disclosure_text || <em>enabled, no text set</em>)
              : 'Recording is off on this line'}
          </Field>
        </>
      )}

      {node.type === NODE.VOICEMAIL && (
        <>
          <Field label="Greeting">{node.greeting || <em>the built-in greeting</em>}</Field>
          <Field label="Transcription">{node.detail}</Field>
        </>
      )}
    </aside>
  );
}

function Field({ label, children }) {
  return (
    <div className="cf-field">
      <span className="cf-field-label">{label}</span>
      <div className="cf-field-value">{children}</div>
    </div>
  );
}

function labelFor(id, flow) {
  if (!id) return <em>nowhere — this is a dead end</em>;
  const n = (flow.nodes || []).find((x) => x.id === id);
  return n ? n.title : id;
}

export default function CallFlow() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('hours');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/settings/voice-routing/');
      setData(res.data);
    } catch (e) {
      // Name the cause, never "something went wrong".
      const status = e?.response?.status;
      setError(
        status === 403 ? 'Your role cannot view answering rules.'
          : status === 401 ? 'Your session expired — sign in again.'
          : e?.response?.data?.detail || 'Could not load the call flow.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flow = useMemo(() => (data ? buildFlow(data) : null), [data]);
  const checks = useMemo(() => (flow ? validateFlow(flow) : []), [flow]);
  const node = flow ? flow.nodes.find((n) => n.id === selected) : null;

  if (loading) return <div className="cf-state">Loading the live call flow…</div>;
  if (error) return <div className="cf-state cf-state--bad">{error}</div>;
  if (!flow) return null;

  const columns = groupIntoColumns(flow);
  const number = (data.numbers || [])[0];

  return (
    <div className="cf">
      <div className="cf-head">
        <div>
          <h3>Main line</h3>
          <span className="cf-num">{number ? number.phone_number : 'No number on this tenant'}</span>
        </div>
        <span className="cf-live">Live · read-only</span>
      </div>

      <div className="cf-editor">
        <div className="cf-canvas">
          <div className="cf-flow">
            {columns.map((col, i) => (
              <React.Fragment key={col.key}>
                {i > 0 && <Arrow />}
                <div className="cf-col">
                  <span className="cf-col-label">{col.label}</span>
                  {col.nodes.map((n) => (
                    <NodeCard key={n.id} node={n} selected={n.id === selected} onSelect={setSelected} />
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        <Inspector node={node} flow={flow} />
      </div>

      <div className="cf-validate">
        {checks.map((c) => (
          <div className={`cf-vrow cf-vrow--${c.level}`} key={c.id}>
            <span className="cf-vmark" aria-hidden="true">
              {c.level === 'pass' ? '✓' : '!'}
            </span>
            <span className="cf-vtext">{c.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Entry → what answers → where it hands off → terminal. */
function groupIntoColumns(flow) {
  const by = (t) => flow.nodes.filter((n) => n.type === t);
  const cols = [
    { key: 'entry', label: 'Entry', nodes: by(NODE.HOURS) },
    { key: 'answers', label: 'Answers', nodes: [...by(NODE.MENU), ...by(NODE.AI)] },
    { key: 'people', label: 'People', nodes: by(NODE.RING) },
    { key: 'terminal', label: 'Terminal', nodes: by(NODE.VOICEMAIL) },
  ];
  return cols.filter((c) => c.nodes.length);
}
